import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import Modal from '../../UI/Modal/Modal';
import ConfirmModal from '../../UI/ConfirmModal/ConfirmModal';
import tbl from '../../../assets/Style/Table.module.css';
import cl from './AuctionFeesList.module.css';
import { selectStyle } from './selectStyle';
import { useAuctionFee } from '../../../hooks/useAuctionFee';

const emptyFee = {
  id: 0,
  min_bid: '',
  max_bid: '',
  fee: '',
  procent: '',
  auction: '',
};

const auctionOptions = [
  { value: 'iaai', label: 'iaai' },
  { value: 'copart', label: 'copart' },
  { value: 'auctionwini', label: 'auctionwini' },
];

const AuctionFeesList = () => {
  const { auctionFeeService } = useAuctionFee();
  const { t } = useTranslation();
  const [formData, setFormData] = useState(emptyFee);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [auctionFilter, setAuctionFilter] = useState(null);
  const isEdit = useMemo(() => formData.id !== 0, [formData.id]);
  const selectedAuction = useMemo(
    () => auctionOptions.find((option) => option.value === formData.auction) ?? null,
    [formData.auction]
  );
  const filteredItems = useMemo(() => {
    if (!auctionFilter) return auctionFeeService.items;
    return auctionFeeService.items.filter((item) => item.auction === auctionFilter.value);
  }, [auctionFeeService.items, auctionFilter]);

  useEffect(() => {
    auctionFeeService.fetch();
  }, []);

  const openCreate = () => {
    setFormData(emptyFee);
    setIsModalOpen(true);
  };

  const openEdit = (item) => {
    setFormData({
      id: item.id,
      min_bid: item.min_bid ?? '',
      max_bid: item.max_bid ?? '',
      fee: item.fee ?? '',
      procent: item.procent ?? '',
      auction: item.auction || '',
    });
    setIsModalOpen(true);
  };

  const openDelete = (item) => {
    setFormData(item);
    setIsDeleteOpen(true);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const save = async () => {
    const payload = {
      ...formData,
      min_bid: parseFloat(formData.min_bid || 0),
      max_bid: parseFloat(formData.max_bid || 0),
      fee: parseFloat(formData.fee || 0),
      procent: parseFloat(formData.procent || 0),
    };

    if (isEdit) {
      await auctionFeeService.update(payload);
    } else {
      await auctionFeeService.add(payload);
    }

    setIsModalOpen(false);
  };

  const confirmDelete = async () => {
    await auctionFeeService.remove(formData.id);
    setIsDeleteOpen(false);
  };

  return (
    <div className={cl.wrapper}>
      <div className={cl.header}>
        <h1>{t('AuctionFees')}</h1>
        <div className={cl.headerActions}>
          <Select
            options={auctionOptions}
            value={auctionFilter}
            onChange={setAuctionFilter}
            styles={selectStyle}
            isClearable
            placeholder={t('FilterByAuction')}
            className={cl.filterSelect}
          />
          <button className={cl.addBtn} onClick={openCreate}>
            + {t('AddFee')}
          </button>
        </div>
      </div>

      <div className={tbl.table}>
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>{t('MinBid')}</th>
              <th>{t('MaxBid')}</th>
              <th>{t('Fee')}</th>
              <th>%</th>
              <th>{t('Auction')}</th>
              <th>{t('Actions')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.map((item) => (
              <tr key={item.id}>
                <td>{item.id}</td>
                <td>{item.min_bid}</td>
                <td>{item.max_bid}</td>
                <td>{item.fee}</td>
                <td>{item.procent}</td>
                <td className={cl.text}>{item.auction}</td>
                <td>
                  <div className={cl.actions}>
                    <button className={cl.linkBtn} onClick={() => openEdit(item)}>
                      {t('Edit')}
                    </button>
                    <button className={cl.linkBtn} onClick={() => openDelete(item)}>
                      {t('Delete')}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filteredItems.length === 0 && (
              <tr>
                <td colSpan={7} className={cl.empty}>
                  {t('NoAuctionFeesYet')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal size="medium" isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <div className={cl.modalHead}>
          <h3>{isEdit ? t('EditAuctionFee') : t('AddAuctionFee')}</h3>
        </div>

        <div className={cl.form}>
          <div className={cl.inlineFields}>
            <label className={cl.field}>
              <span>{t('MinBid')}</span>
              <input
                type="number"
                step="0.01"
                name="min_bid"
                value={formData.min_bid}
                onChange={handleChange}
              />
            </label>
            <label className={cl.field}>
              <span>{t('MaxBid')}</span>
              <input
                type="number"
                step="0.01"
                name="max_bid"
                value={formData.max_bid}
                onChange={handleChange}
              />
            </label>
          </div>

          <div className={cl.inlineFields}>
            <label className={cl.field}>
              <span>{t('Fee')}</span>
              <input
                type="number"
                step="0.01"
                name="fee"
                value={formData.fee}
                onChange={handleChange}
              />
            </label>
            <label className={cl.field}>
              <span>{t('Procent')}</span>
              <input
                type="number"
                step="0.01"
                name="procent"
                value={formData.procent}
                onChange={handleChange}
              />
            </label>
          </div>

          <label className={cl.field}>
            <span>{t('Auction')}</span>
            <Select
              options={auctionOptions}
              value={selectedAuction}
              onChange={(option) =>
                setFormData((prev) => ({
                  ...prev,
                  auction: option?.value || '',
                }))
              }
              styles={selectStyle}
              isClearable
              placeholder={t('SelectAuction')}
            />
          </label>
        </div>

        <div className={cl.modalActions}>
          <button className={cl.secondaryBtn} onClick={() => setIsModalOpen(false)}>
            {t('Cancel')}
          </button>
          <button className={cl.primaryBtn} onClick={save}>
            {isEdit ? t('Update') : t('Create')}
          </button>
        </div>
      </Modal>

      <ConfirmModal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={confirmDelete}
        title={t('DeleteAuctionFee')}
        message={t('DeleteItemNumber', { id: formData.id })}
        confirmText={t('Delete')}
        cancelText={t('Cancel')}
      />
    </div>
  );
};

export default AuctionFeesList;
